import { memo, useCallback, useMemo, useState } from 'react';
import type { BlurEvent } from 'react-native';
import { Stack, VStack } from '@adc/parallax-component-library';
import { type FormikErrors, type FormikTouched } from 'formik';

import {
  InputErrorField,
  InputPassword,
  PasswordRequirements,
} from '~/components/utility';

type Props = {
  fieldName: string;
  placeholder: string;
  label: string;
  handleBlur: (e?: BlurEvent) => void;
  onChangeText: (value: string) => void;
  password: string;
  onValidate?: (meetsPasswordRequirements: boolean) => void;
  identities?: Identity[];
  testID?: string;
  errors: FormikErrors<Record<string, string>>;
  touched: FormikTouched<Record<string, string>>;
  disabled?: boolean;
};

export const PasswordInputValidator = memo(
  ({
    placeholder,
    label,
    handleBlur,
    onChangeText,
    password,
    onValidate,
    errors,
    touched,
    identities = [],
    testID,
    disabled,
    fieldName,
  }: Props) => {
    const [isBlurred, setIsBlurred] = useState(false);
    const [meetsPasswordRequirements, setMeetsPasswordRequirements] =
      useState(false);

    const onFocus = useCallback(() => {
      setIsBlurred(false);
    }, []);

    const filteredIdentities = useMemo(() => {
      const keysToFilter = ['min'];

      return identities.filter(({ data: { key } }) =>
        key && typeof key === 'string' ? keysToFilter.includes(key) : false,
      );
    }, [identities]);

    const requirements: Requirement[] = useMemo(() => {
      return filteredIdentities.map(({ data, ...rest }) => {
        // @ts-expect-error fix typing for data
        const match = new RegExp(data.regex).test(password.trim());

        const isInvalid = isBlurred && !match;

        return { match, isInvalid, data, ...rest };
      });
    }, [password, filteredIdentities, isBlurred]);

    const validatePassword = useCallback(() => {
      const remainingRequirements = requirements.filter(
        (requirement) => !requirement.match,
      );

      const meetsPasswordRequirement = !remainingRequirements.length;

      setMeetsPasswordRequirements(meetsPasswordRequirement);

      onValidate?.(meetsPasswordRequirement);
    }, [requirements, onValidate]);

    const onBlur = useCallback(
      (e?: BlurEvent) => {
        setIsBlurred(true);
        validatePassword();
        handleBlur(e);
      },
      [handleBlur, validatePassword],
    );

    const hasErrors = !!(errors[fieldName] && touched[fieldName]);

    return (
      <Stack>
        <InputPassword
          isInvalid={(!meetsPasswordRequirements && isBlurred) || hasErrors}
          placeholder={placeholder}
          label={label}
          testID={testID}
          onBlur={onBlur}
          onChangeText={onChangeText}
          defaultValue={password}
          onFocus={onFocus}
          disabled={disabled}
        />
        <VStack gap="$2">
          <PasswordRequirements requirements={requirements} testID={testID} />
          {hasErrors && (
            <InputErrorField errorMessage={errors[fieldName]} testID={testID} />
          )}
        </VStack>
      </Stack>
    );
  },
);

PasswordInputValidator.displayName = 'PasswordInputValidator';
